import { Link as RouterLink } from 'react-router-dom';
import { Box, Chip, IconButton, Link, Stack, Tooltip, Typography } from '@mui/material';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import type { Product } from '@eprintsmart/shared';

/**
 * The products sitting directly on one category node.
 *
 * Archived products stay visible, dimmed and sorted last, rather than being
 * filtered out. Hiding them turns the archive into a black hole — you cannot
 * restore what you cannot see — and the volume does not justify a filter yet.
 *
 * The archive action uses an archive icon, deliberately not the delete icon the
 * category rows above use: those really do delete, and two different outcomes
 * must not share a glyph.
 */
export function CategoryProductRows({
  products,
  depth,
  onImages,
  onArchive,
  onRestore,
}: {
  products: Product[];
  depth: number;
  onImages: (product: Product) => void;
  onArchive: (product: Product) => void;
  onRestore: (product: Product) => void;
}) {
  if (products.length === 0) return null;

  const sorted = [...products].sort((a, b) => {
    const aArchived = a.status === 'ARCHIVED' ? 1 : 0;
    const bArchived = b.status === 'ARCHIVED' ? 1 : 0;
    return aArchived - bArchived || a.name.localeCompare(b.name);
  });

  return (
    <>
      {sorted.map((p) => {
        const archived = p.status === 'ARCHIVED';
        const imageCount = p.imageIds?.length ?? 0;
        return (
          <Stack
            key={p.id}
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              pl: depth * 3 + 5,
              py: 0.4,
              borderRadius: 1,
              opacity: archived ? 0.6 : 1,
              '&:hover': { bgcolor: 'action.hover' },
              '&:hover .product-actions': { opacity: 1 },
              // Actions are visible at rest (see below); hover only brightens
              // them. Hiding them entirely meant that on a list of 40 products
              // exactly one -- whichever the cursor happened to be over --
              // looked like it supported uploading an image.
            }}
          >
            {/* Aligns product names under category names, past the chevron. */}
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'text.disabled', flexShrink: 0 }} />

            <Link
              component={RouterLink}
              to={`/catalog/products/${p.id}`}
              underline="hover"
              sx={{ fontSize: '0.9rem' }}
            >
              {p.name}
            </Link>

            {p.status !== 'ACTIVE' && (
              <Chip size="small" label={p.status} variant="outlined" sx={{ height: 18, fontSize: 10 }} />
            )}

            {/* A product with no images renders as a blank tile on the
                storefront, so it is worth flagging where products are listed. */}
            {imageCount === 0 ? (
              <Chip size="small" color="warning" variant="outlined" label="no images" sx={{ height: 18, fontSize: 10 }} />
            ) : (
              <Typography variant="caption" color="text.secondary">
                {imageCount} image{imageCount === 1 ? '' : 's'}
              </Typography>
            )}

            <Stack
              direction="row"
              spacing={0.5}
              className="product-actions"
              sx={{ ml: 'auto', opacity: 0.55, transition: 'opacity 120ms' }}
            >
              <Tooltip title="Images">
                <IconButton size="small" onClick={() => onImages(p)}>
                  <ImageOutlinedIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit product">
                <IconButton size="small" component={RouterLink} to={`/catalog/products/${p.id}`}>
                  <EditOutlinedIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
              {archived ? (
                <Tooltip title="Restore as draft">
                  <IconButton size="small" onClick={() => onRestore(p)}>
                    <UnarchiveOutlinedIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Archive">
                  <IconButton size="small" onClick={() => onArchive(p)}>
                    <ArchiveOutlinedIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Stack>
        );
      })}
    </>
  );
}
